import React, { useState } from 'react';
import { Flex } from 'rebass';
import {
  FormComboGroup,
  Input,
  SearchInput,
  Select,
} from '../../components/Form';
import Button from '../../components/Button/Button';
import PropTypes from 'prop-types';
import { isBrowser } from 'react-device-detect';
import { AssignmentStatus } from '../../types/assignment';
import useMode from '../../hooks/useMode';
import { FilterBarProps } from '../../components/FilterBar/FilterBar';
import { BackflowTestFlagsPicker } from '../../components/BackflowTest/BackflowTestFlagsPicker';
import { JobStatusPicker } from '../../components/Job/JobStatusPicker';
import { MonthDayPicker } from '../../components/Date/MonthDayPicker';
import { getStringSearchPlaceholder } from '../../helpers/search';

const resultOptions = [
  {
    value: 'ALL',
    label: '- result -',
  },
  {
    value: 'PASS',
    label: 'Pass',
  },
  {
    value: 'FAIL',
    label: 'Fail',
  },
  {
    value: 'FAILPASS',
    label: 'Fail/Pass',
  },
] as const;

export const stringSearchFields = [
  {
    field: 'jobId',
    label: 'Job',
  },
  {
    field: 'job.address.street1',
    label: 'Address',
  },
  {
    field: 'job.address.street2',
    label: null,
  },
  {
    field: 'job.backflowTest.backflowAssembly.serialNumber',
    label: 'Serial',
  },
  {
    field: 'customerProfile.name',
    label: 'Customer',
  },
  {
    field: 'job.backflowTest.backflowAssembly.waterService.pin',
    label: 'TRAC',
  },
] as const;

export const stringSearchFieldsCompany = [
  ...stringSearchFields,
  {
    field: 'child.vendorProfile.name',
    label: 'Tester',
  },
  {
    field: 'job.backflowTest.waterSupplierProfile.name',
    label: 'Water Supplier',
  },
] as const;

export const dateOptions = [
  {
    field: 'created',
    label: 'Ordered',
  },
  {
    field: 'assigned',
    label: 'Assigned',
  },
  {
    field: 'completed',
    label: 'Completed',
  },
  {
    field: 'job.backflowTest.dateTested',
    label: 'Tested',
  },
  {
    field: 'submitted',
    label: 'Submitted',
  },
] as const;

export type JobFilters = {
  status?: AssignmentStatus[];
  testResult?: string;
  stringSearch?: string;
  currentDateFilterField?: string;
  dateStart?: string;
  dateEnd?: string;
  flags?: string[];
};

export const defaultFilters: JobFilters = {
  status: ['OPEN'],
};

type FilterBarJobProps = FilterBarProps<JobFilters>;

const FilterBarJob: React.FC<FilterBarJobProps> = (props) => {
  const { filters, setFilters, selectedId } = props;

  const mode = useMode();

  const onChange = (e: any) => {
    setFilters((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  };

  const [dateAutoFocus] = useState(!filters.currentDateFilterField);

  const searchFields =
    mode === 'company' ? stringSearchFieldsCompany : stringSearchFields;

  return (
    <Flex variant="filterBar.container" {...(props as any)}>
      <Flex variant="filterBar.left">
        <SearchInput
          placeholder={getStringSearchPlaceholder(searchFields)}
          autoFocus={isBrowser && !selectedId}
          width={400}
          name="stringSearch"
          value={filters.stringSearch || ''}
          onChange={onChange}
        />
        <JobStatusPicker
          value={filters.status}
          onChange={(values) => {
            setFilters({
              ...filters,
              status: values,
            });
          }}
        />
        <BackflowTestFlagsPicker
          value={filters.flags}
          onChange={(values) => {
            if (Array.isArray(values)) {
              setFilters({
                ...filters,
                flags:
                  values.length > 0
                    ? values.map((value1) => value1.value)
                    : undefined,
              });
            }
          }}
        />
        <FormComboGroup>
          <Select
            name="currentDateFilterField"
            value={filters.currentDateFilterField || 'ALL'}
            onChange={onChange}
            variant={
              filters.currentDateFilterField &&
              filters.currentDateFilterField !== 'ALL'
                ? 'inputRoundedLeft'
                : 'input'
            }
            width={130}
          >
            <option value="ALL">- date -</option>
            {dateOptions.map((dateOption, i) => (
              <option value={dateOption.field} key={i}>
                {dateOption.label}
              </option>
            ))}
          </Select>
          {filters.currentDateFilterField &&
            filters.currentDateFilterField !== 'ALL' && (
              <MonthDayPicker
                startDate={filters.dateStart}
                endDate={filters.dateEnd}
                autoFocus={dateAutoFocus}
                variant="inputRoundedRight"
                onChange={(dateStart, dateEnd) => {
                  setFilters((prevState) => ({
                    ...prevState,
                    dateStart: dateStart ?? undefined,
                    dateEnd: dateEnd ?? undefined,
                  }));
                }}
              />
            )}
        </FormComboGroup>
      </Flex>

      <Flex variant="filterBar.right">
        <Button
          variant="outline"
          icon="undo"
          onClick={() => setFilters(defaultFilters)}
          title="Reset Form"
        />
      </Flex>
    </Flex>
  );
};

FilterBarJob.propTypes = {
  filters: PropTypes.any,
  setFilters: PropTypes.func.isRequired,
};

export default FilterBarJob;
